import { useSyncExternalStore } from 'react';
import { API_NOTIFICATION } from './EsConstants';
import { esPut, esRead } from './EsFetch';

/**
 * The bell's unread count, as one fact the nav bar reads (esback's EsNotifyService).
 *
 * <p>One poll for the page, EsServerStatus's shape: it runs only while a bell is drawn, and asks
 * only while the tab is visible -- a hidden tab costs nothing and asks again the moment it shows.
 * Every 30 s, a count over the reader's unread rows on a partial index: the cheapest question
 * esback answers. The list itself is read only when the bell opens.
 *
 * <p>esNotificationsRefresh asks at once -- after the bell marks a row, or the Commons reads a
 * line, which reads that chat's notices too -- coalesced, so a burst of reads asks once.
 */

const POLL_MS = 30000;
const SOON_MS = 1500;

let unread = 0;
let poll = null;
let soon = null;
let asking = false;
const listeners = new Set();

function set( pUnread ) {
    if ( unread === pUnread ) return;
    unread = pUnread;
    listeners.forEach( ( aListener ) => aListener() );
}

async function ask() {
    if ( asking || listeners.size === 0 || document.visibilityState === 'hidden' ) return;
    asking = true;
    try {
        const aCount = await esRead( API_NOTIFICATION + '/count' );
        set( Number.isFinite( aCount?.unread ) ? aCount.unread : 0 );
    }
    catch {
        // No answer is no news: the badge keeps what it had, and the next poll asks again.
    }
    finally {
        asking = false;
    }
}

const onVisible = () => { if ( document.visibilityState === 'visible' ) void ask(); };

function subscribe( pListener ) {
    listeners.add( pListener );
    if ( !poll ) {
        poll = setInterval( ask, POLL_MS );
        document.addEventListener( 'visibilitychange', onVisible );
        void ask();
    }
    return () => {
        listeners.delete( pListener );
        if ( listeners.size === 0 ) {
            clearInterval( poll );
            clearTimeout( soon );
            poll = null;
            soon = null;
            document.removeEventListener( 'visibilitychange', onVisible );
            set( 0 );
        }
    };
}

const subscribeNothing = () => () => {};

/** The badge's number; 0, and no poll, while pOn is false. */
export function useEsNotificationCount( pOn ) {
    return useSyncExternalStore( pOn ? subscribe : subscribeNothing, () => ( pOn ? unread : 0 ), () => 0 );
}

/** Ask for the count soon, once, however many callers ask. */
export function esNotificationsRefresh() {
    if ( listeners.size === 0 || soon ) return;
    soon = setTimeout( () => {
        soon = null;
        void ask();
    }, SOON_MS );
}

/** The count a list answered, so the badge agrees with what the dropdown shows. */
export function esNotificationsKnown( pUnread ) {
    if ( Number.isFinite( pUnread ) ) set( pUnread );
}

export const esNotificationList = () => esRead( API_NOTIFICATION );
export const esNotificationRead = ( pId ) => esPut( API_NOTIFICATION + '/' + pId + '/read' );
export const esNotificationsAllRead = () => esPut( API_NOTIFICATION + '/read' );

/** The badge's words: 99+ past 99, so the disc never grows. */
export const esBadgeText = ( pCount ) => ( pCount > 99 ? '99+' : String( pCount ) );
